/**
 * ClientTransferDrawer — right-side slide-in drawer containing the 2-step
 * client transfer wizard (was ClientTransferPage).
 *
 * Shell pattern matches BulkBcbaPanel / TeamMemberDetailDrawer:
 *   fixed Overlay (zIndex.modalBackdrop) + Panel (zIndex.modal) that slides
 *   in from the right, sticky Header, scrollable Body, sticky Footer.
 *
 * Width: form wizards are narrower than detail drawers.
 *   desktop  (>1024px) : min(560px, 100vw)
 *   landscape tablet (≤1024px) : min(92vw, 560px)  → effectively 92vw
 *   portrait tablet / mobile (≤768px) : 100vw
 *
 * Body scroll is locked while the drawer is open.
 * Closes on Escape, overlay click, or the X button.
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { X, Building2, AlertCircle, CheckCircle, ChevronRight, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { media } from '../../styles/media';
import locationService from '../../services/api/locationService';
import locationTransferService from '../../services/api/locationTransferService';
import {
  StepIndicatorRow,
  StepItem,
  StepBubble,
  StepLabel,
  StepConnector,
  StepTag,
  WizardCard,
  CardTitle,
  CardDescription,
  InfoRow,
  InfoRowText,
  InfoRowLabel,
  InfoRowValue,
  FormGroup,
  FieldWrapper,
  FieldLabel,
  RequiredStar,
  Textarea,
  FieldError,
  ErrorAlert,
  BottomActions,
} from '../TransferWizard/WizardShell';
import { AssignmentReviewStep, type BcbaDecision, type RbtDecision } from '../ClientTransferPage/AssignmentReviewStep';

// ==================== Types ====================

interface Step1FormValues {
  toCenterId: string;
  reason: string;
}

export interface ClientTransferDrawerProps {
  centerId: string;
  clientId: string;
  clientName?: string;
  onClose: () => void;
  onTransferred: () => void;
}

const STEPS = ['Transfer Details', 'Assignment Review'] as const;

// ==================== Animations ====================

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(560px, 100vw);
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: min(92vw, 560px);
  }

  ${media.tablet} {
    width: 100vw;
  }
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const DrawerTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DrawerSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const LoaderBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  flex: 1;
  min-height: 200px;
`;

const LoaderText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// Strip the outer padding/shadow from WizardCard when inside the drawer body
const InlineCard = styled(WizardCard)`
  border: none;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
  background: transparent;
  animation: none;
`;

// ==================== Component ====================

export const ClientTransferDrawer = ({
  centerId,
  clientId,
  clientName,
  onClose,
  onTransferred,
}: ClientTransferDrawerProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [step1Values, setStep1Values] = useState<Step1FormValues | null>(null);
  const [bcbaDecision, setBcbaDecision] = useState<BcbaDecision>({ action: 'keep' });
  const [rbtDecisions, setRbtDecisions] = useState<RbtDecision[]>([]);
  const [apiError, setApiError] = useState('');

  // Lock body scroll while drawer is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on Escape — use a ref so the listener never re-registers on parent re-renders
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const { data: sourceCenter } = useQuery({
    queryKey: ['location', centerId],
    queryFn: () => locationService.getLocationDetail(centerId),
  });

  const { data: locationsData, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations-active'],
    queryFn: () => locationService.getLocations({ limit: 100, status: 'active' }),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step1FormValues>({ defaultValues: { toCenterId: '', reason: '' } });

  const { mutate: doTransfer, isPending } = useMutation({
    mutationFn: (values: Step1FormValues) =>
      locationTransferService.transferClient({
        clientId,
        toCenterId: values.toCenterId,
        reason: values.reason,
        bcbaDecision,
        rbtDecisions,
      }),
    onSuccess: () => {
      onTransferred();
      onClose();
    },
    onError: (err: any) => {
      setApiError(err?.response?.data?.message || 'Transfer failed. Please try again.');
    },
  });

  const destinationOptions = useMemo(
    () => [
      { value: '', label: 'Select destination center...' },
      ...(locationsData?.data ?? [])
        .filter((loc) => loc.isActive && loc.id !== centerId)
        .map((loc) => ({ value: loc.id, label: `${loc.name} — ${loc.city}, ${loc.state}` })),
    ],
    [locationsData, centerId],
  );

  const selectedCenterId = watch('toCenterId');
  const selectedCenter = locationsData?.data.find((l) => l.id === selectedCenterId);

  const handleRbtDecisionChange = (memberId: string, action: 'keep' | 'remove') => {
    setRbtDecisions((prev) => {
      const idx = prev.findIndex((d) => d.rbtMemberId === memberId);
      if (idx >= 0) {
        if (prev[idx].action === action) return prev;
        const next = [...prev];
        next[idx] = { rbtMemberId: memberId, action };
        return next;
      }
      return [...prev, { rbtMemberId: memberId, action }];
    });
  };

  const handleStep1Submit = (values: Step1FormValues) => {
    setStep1Values(values);
    setStep(2);
  };

  return (
    <Overlay onClick={onClose}>
      <Panel
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Transfer client: ${clientName ?? 'Client'}`}
      >
        <Header>
          <TitleGroup>
            <DrawerTitle>Transfer Client: {clientName ?? 'Client'}</DrawerTitle>
            <DrawerSubtitle>
              Move this client to a new center and review provider assignments.
            </DrawerSubtitle>
          </TitleGroup>
          <CloseBtn onClick={onClose} aria-label="Close drawer">
            <X />
          </CloseBtn>
        </Header>

        <Body>
          {locationsLoading ? (
            <LoaderBox>
              <LoadingSpinner size="lg" />
              <LoaderText>Loading transfer details...</LoaderText>
            </LoaderBox>
          ) : (
            <>
              <StepIndicatorRow>
                {STEPS.map((label, i) => {
                  const status = i + 1 < step ? 'completed' : i + 1 === step ? 'active' : 'upcoming';
                  return (
                    <StepItem key={label}>
                      <StepBubble $status={status}>
                        {status === 'completed' ? <CheckCircle /> : i + 1}
                      </StepBubble>
                      <StepLabel $active={status === 'active'}>{label}</StepLabel>
                      {i < STEPS.length - 1 && <StepConnector $done={i + 1 < step} />}
                    </StepItem>
                  );
                })}
              </StepIndicatorRow>

              <StepTag>
                STEP {step} OF {STEPS.length} | <span>{STEPS[step - 1]}</span>
              </StepTag>

              {step === 1 && (
                <InlineCard key="step1">
                  <CardTitle>Transfer Details</CardTitle>
                  <CardDescription>
                    Select the destination center and provide a reason for this transfer.
                  </CardDescription>

                  {sourceCenter && (
                    <InfoRow>
                      <Building2 />
                      <InfoRowText>
                        <InfoRowLabel>Current Center</InfoRowLabel>
                        <InfoRowValue>
                          {sourceCenter.name} — {sourceCenter.city}, {sourceCenter.state}
                        </InfoRowValue>
                      </InfoRowText>
                    </InfoRow>
                  )}

                  {apiError && (
                    <ErrorAlert>
                      <AlertCircle />
                      <span>{apiError}</span>
                    </ErrorAlert>
                  )}

                  <form onSubmit={handleSubmit(handleStep1Submit)} noValidate>
                    <FormGroup>
                      <FieldWrapper>
                        <FieldLabel htmlFor="toCenterId">
                          Destination Center
                          <RequiredStar>*</RequiredStar>
                        </FieldLabel>
                        <Select
                          id="toCenterId"
                          options={destinationOptions}
                          error={errors.toCenterId?.message}
                          fullWidth
                          {...register('toCenterId', {
                            required: 'Please select a destination center',
                          })}
                        />
                        {errors.toCenterId && (
                          <FieldError>{errors.toCenterId.message}</FieldError>
                        )}
                      </FieldWrapper>

                      {selectedCenter && (
                        <InfoRow>
                          <MapPin />
                          <InfoRowText>
                            <InfoRowLabel>Transferring To</InfoRowLabel>
                            <InfoRowValue>
                              {selectedCenter.name} — {selectedCenter.city}, {selectedCenter.state}
                            </InfoRowValue>
                          </InfoRowText>
                        </InfoRow>
                      )}

                      <FieldWrapper>
                        <FieldLabel htmlFor="reason">
                          Reason for Transfer
                          <RequiredStar>*</RequiredStar>
                        </FieldLabel>
                        <Textarea
                          id="reason"
                          placeholder="Provide a reason for this transfer..."
                          rows={4}
                          $hasError={Boolean(errors.reason)}
                          {...register('reason', {
                            required: 'A reason is required',
                            minLength: {
                              value: 10,
                              message: 'Please provide at least 10 characters',
                            },
                          })}
                        />
                        {errors.reason && <FieldError>{errors.reason.message}</FieldError>}
                      </FieldWrapper>
                    </FormGroup>

                    <BottomActions>
                      <Button variant="ghost" type="button" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button variant="primary" type="submit" rightIcon={<ChevronRight />}>
                        Continue to Review
                      </Button>
                    </BottomActions>
                  </form>
                </InlineCard>
              )}

              {step === 2 && step1Values && (
                <InlineCard key="step2">
                  <CardTitle>Review Provider Assignments</CardTitle>
                  <CardDescription>
                    Decide what to do with the existing BCBA and RBT assignments for this client.
                  </CardDescription>

                  {apiError && (
                    <ErrorAlert>
                      <AlertCircle />
                      <span>{apiError}</span>
                    </ErrorAlert>
                  )}

                  <AssignmentReviewStep
                    newCenterName={selectedCenter?.name ?? 'new center'}
                    currentBcbaCenterName={sourceCenter?.name}
                    rbts={[]}
                    bcbaDecision={bcbaDecision}
                    rbtDecisions={rbtDecisions}
                    onBcbaDecisionChange={setBcbaDecision}
                    onRbtDecisionChange={handleRbtDecisionChange}
                    onComplete={() => doTransfer(step1Values)}
                    onBack={() => setStep(1)}
                    isSubmitting={isPending}
                  />
                </InlineCard>
              )}
            </>
          )}
        </Body>
      </Panel>
    </Overlay>
  );
};

export default ClientTransferDrawer;
